import { encodeWavBase64 } from './wavEncode.js';
export class TtsWorkerClient {
    worker;
    nextId = 1;
    pending = new Map();
    constructor(worker) {
        this.worker = worker;
        this.worker.addEventListener('message', (ev) => this.onMessage(ev.data));
        this.worker.addEventListener('error', (ev) => {
            const message = `tts worker error: ${ev.message}`;
            for (const [, p] of this.pending)
                p.reject(new Error(message));
            this.pending.clear();
        });
    }
    onMessage(msg) {
        const pending = this.pending.get(msg.id);
        if (!pending)
            return;
        if (msg.type === 'progress') {
            // Non-terminal: a warmup can report many of these before its
            // matching `ready`/`error` — don't delete the pending entry.
            if (pending.kind === 'warmup')
                pending.onProgress?.(msg);
            return;
        }
        this.pending.delete(msg.id);
        if (msg.type === 'error') {
            pending.reject(new Error(msg.message));
            return;
        }
        if (msg.type === 'ready' && pending.kind === 'warmup') {
            pending.resolve(msg.engine);
            return;
        }
        if (msg.type === 'voices' && pending.kind === 'voices') {
            pending.resolve(msg.voices);
            return;
        }
        if (msg.type === 'result' && pending.kind === 'synthesize') {
            console.debug(`[legion-speech] tts client: encoding WAV for #${msg.id} on main thread (${msg.audio.length} samples @ ${msg.sampleRate}Hz)…`);
            const audioBase64 = encodeWavBase64(msg.audio, msg.sampleRate);
            pending.resolve({
                audioBase64,
                mimeType: 'audio/wav',
                sampleRate: msg.sampleRate,
                voice: msg.voice,
                engine: msg.engine,
                durationMs: msg.durationMs,
            });
            return;
        }
        // Response type doesn't match the pending request's kind — a bug in
        // this client's id bookkeeping, not something a caller can act on.
        pending.reject(new Error(`tts worker: mismatched response type "${msg.type}" for pending "${pending.kind}" request`));
    }
    synthesize(args) {
        const id = this.nextId++;
        console.debug(`[legion-speech] tts client: synthesize #${id} — "${args.text.slice(0, 60)}"`);
        const req = {
            type: 'synthesize',
            id,
            text: args.text,
            voice: args.voice,
            language: args.language,
        };
        return new Promise((resolve, reject) => {
            this.pending.set(id, { kind: 'synthesize', resolve, reject });
            // Structured-clone COPY (no transferable list) — see ttsWorker.ts's
            // doc: the request itself carries no large payload (just text), so
            // there's nothing to transfer either way.
            this.worker.postMessage(req);
        });
    }
    /** List voice ids this peer's TTS engine supports. Resolves once the engine has loaded. */
    listVoices() {
        const id = this.nextId++;
        const req = { type: 'listVoices', id };
        return new Promise((resolve, reject) => {
            this.pending.set(id, { kind: 'voices', resolve, reject });
            this.worker.postMessage(req);
        });
    }
    /**
     * Load the Kokoro engine without synthesizing anything — resolves once
     * the worker reports `ready`, rejects on `error`. `onProgress` is
     * called for every `progress` message in between (a Cache Storage hit
     * may skip straight to `ready` with none). See module doc.
     */
    warmup(onProgress) {
        const id = this.nextId++;
        console.debug(`[legion-speech] tts client: warmup #${id}`);
        const req = { type: 'warmup', id };
        return new Promise((resolve, reject) => {
            this.pending.set(id, {
                kind: 'warmup',
                resolve: (engine) => {
                    console.debug(`[legion-speech] tts client: warmup #${id} ready (${engine})`);
                    resolve();
                },
                reject,
                onProgress,
            });
            this.worker.postMessage(req);
        });
    }
    /** Terminate the underlying worker and reject any in-flight requests. */
    dispose() {
        for (const [, p] of this.pending)
            p.reject(new Error('tts worker client disposed'));
        this.pending.clear();
        this.worker.terminate();
    }
}
